import { Input, InputProps } from './Input'
import { evaluate } from 'mathjs'
import React, { forwardRef, useEffect, useMemo, useState } from 'react'

const ALLOWED_MATH_CHAR_REGEX = /^[0-9/*+., -]*$/

export interface MathInputProps extends InputProps {
  value: number
}

const round = (number: number, precision: number) =>
  Number.isFinite(number) ? Math.round(number * 10 ** precision) / 10 ** precision : number
const getPrecision = (step: number | string = 1) => (String(step).split('.')[1] ?? '').length

const format = (value: number | null, precision: number) =>
  value !== null && Number.isFinite(value)
    ? round(value, precision).toString().replace('.', ',')
    : ''

const needsEvaluation = (value: string) => {
  const cleaned = value.trim().replace(',', '.')
  return !Number.isFinite(Number(cleaned)) || cleaned.endsWith('.')
}

const evaluateExpression = (
  value: string,
  precision: number,
  { warnOnError = false }: { warnOnError?: boolean } = {}
): number | null => {
  if (!ALLOWED_MATH_CHAR_REGEX.test(value)) return null
  try {
    const res = evaluate(value.replaceAll(',', '.'))
    return Number.isFinite(res) ? round(res, precision) : null
  } catch (e) {
    if (warnOnError) {
      console.warn('MathInput: Invalid expression', e, value)
    }
    return null
  }
}

export const MathInput = forwardRef<HTMLInputElement, MathInputProps>(
  ({ value, step: _step, onChange, ...rest }, ref) => {
    const step = Number(_step)
    const precision = useMemo(() => getPrecision(step), [step])
    const [focused, setFocused] = useState(false)
    const [rawValue, setRawValue] = useState(() => format(value ?? null, precision))

    useEffect(() => {
      if (!focused) setRawValue(format(value ?? null, precision))
    }, [value, focused, precision])

    const commit = (raw: string, finalCommit = false) => {
      const evaluated = evaluateExpression(raw, precision, { warnOnError: finalCommit })

      // CASE: Final commit e.g. after Enter or blur
      // > Clean raw value and set evaluated value
      if (finalCommit) {
        setRawValue(format(evaluated, precision))
        onChange?.(evaluated)
      }
      // CASE: Intermediate commit e.g. onChange and value does not need evaluation
      // > Set raw value and call onChange with evaluated value
      else if (!needsEvaluation(raw)) {
        setRawValue(raw)
        onChange?.(evaluated)
      }
      // CASE: Intermediate commit and value needs evaluation
      // > Set raw value and wait for final commit
      else {
        setRawValue(raw)
      }
    }

    const incrementValue = (step: number) => {
      const num = round((evaluateExpression(rawValue, precision) ?? 0) + step, precision)
      setRawValue(format(num, precision))
      onChange?.(num)
    }

    return (
      <Input
        {...rest}
        ref={ref}
        value={rawValue}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
          rest.onFocus?.(e)
          setFocused(true)
          e.target.select()
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          commit(e.target.value, true)
          setFocused(false)
          rest.onBlur?.(e)
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (ALLOWED_MATH_CHAR_REGEX.test(e.target.value)) commit(e.target.value)
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          switch (e.key) {
            case 'Enter':
              e.preventDefault()
              commit(e.currentTarget.value, true)
              break
            case 'ArrowUp':
              e.preventDefault()
              incrementValue(step)
              break
            case 'ArrowDown':
              e.preventDefault()
              incrementValue(-step)
              break
            case 'Escape':
              e.preventDefault()
              e.currentTarget.blur()
              break
            default:
              rest.onKeyDown?.(e)
          }
        }}
      />
    )
  }
)

export default MathInput
